/**
 * Small status pill in the header that summarizes OPBridge connectivity.
 *
 * Click → opens the Connect popup (the ConnectPage in its own modal).
 * Was routed through Settings → Connect subpage (DEC-060); the Connect
 * surface moved out into its own popup so the pill lands straight on
 * connection management. LAN-scan discovery + inline pair approval.
 *
 * Visual states (driven by `aggregateStatus()`):
 *   - none        : no bridges configured. Dim grey, label "OPBridge".
 *   - connected   : every bridge reached. Cyan dot, label "OPBridge".
 *   - partial     : some bridges reached, some not. Amber dot.
 *   - unreachable : no bridges reached. Red dot — pulses for attention.
 *   - unpaired    : every bridge missing a token. Amber dot, label "pair".
 *   - revoked     : the primary's bearer was revoked (Phase B). Red +
 *     pulses; takes precedence over aggregateStatus  -  a live TCP connection
 *     is worthless if the token's dead, and the fix is re-pair, not retry.
 */

import { For, Show } from 'solid-js';
import {
  aggregateStatus,
  bridges,
  getConnection,
  connectionsVersion,
} from '../opbridge/store.ts';
import { consumerRevoked, consumerDenied } from '../opbridge/consumerWs.ts';
import { openConnect } from '../settings/store.ts';

export function OpbridgeBadge() {
  function dataState(): string {
    if (consumerRevoked() || consumerDenied()) return 'down';
    switch (aggregateStatus()) {
      case 'connected':   return 'ok';
      case 'partial':     return 'warn';
      case 'unpaired':    return 'warn';
      case 'unreachable': return 'down';
      case 'none':        return 'down';
      default:            return 'down';
    }
  }

  /**
   * Attention level, and the distinction is load-bearing:
   *
   *   'critical' - ZERO working bridges, so the whole rig can send NOTHING.
   *                Flashes red. Operator 2026-07-30, from the iPad: "button
   *                dont seem to work... like it's unpressable". They were
   *                right that nothing worked and wrong about why - the device
   *                had never paired, so every control carried `disabled`, and
   *                the only signals were 0.34 opacity and a small grey pill
   *                reading "not set up". A rig that can do nothing has to say
   *                so loudly; reading it as broken hardware was the app's
   *                fault, not theirs.
   *   'true'     - SOME bridges work. Amber, no flashing: degraded is not the
   *                same as dead, and flashing for both would train the
   *                operator to ignore the flash.
   *
   * `none` was the bug: it is the state a brand-new device is in, i.e. the one
   * that most needs pointing at, and it was the single case excluded here.
   */
  function attention(): 'critical' | 'true' | undefined {
    if (consumerRevoked() || consumerDenied()) return 'critical';
    switch (aggregateStatus()) {
      case 'none':        return 'critical';   // never paired on this device
      case 'unpaired':    return 'critical';   // bridges known, no token: sends nothing
      case 'unreachable': return 'critical';   // paired but nothing answers
      case 'partial':     return 'true';       // degraded, still usable
      default:            return undefined;
    }
  }

  function summary(): string {
    if (consumerRevoked()) return 'revoked';
    // Distinct word, because the operator's next action differs: 'revoked'
    // can clear itself on a refresh, this one only clears on a re-pair.
    if (consumerDenied()) return 'missing permission';
    // Register a dependency on connection SWAPS before reading the map.
    // Without this the badge reports "connected" indefinitely through a real
    // outage: `getConnection()` is a Map lookup Solid cannot track, and the
    // connection object is replaced on reconnect, so the subscription taken
    // here stays pointed at a destroyed object whose status never changes
    // again. Measured 2026-09-11: 147 seconds fully offline, every control
    // disabled, `[opbridge] connected -> unreachable` in the console, and this
    // pill still reading "OPBridge CONNECTED".
    //
    // That is not cosmetic. It is the single indicator the operator glances at
    // mid-set, and a rig that says CONNECTED while nothing works reads as a
    // frozen app - which is exactly how the 2026-09-10 gig incident was
    // interpreted, and why CP-130 recorded a "stale 2/2 up badge" as evidence
    // the page's JS had died. It had not; this had.
    connectionsVersion();
    const cfgs = bridges();
    // "tap to connect", not "not set up": a never-paired device needs to be
    // told what to DO, and this pill IS the button that does it. The old copy
    // described a state and left the operator to infer the action.
    if (cfgs.length === 0) return 'tap to connect';
    const total = cfgs.length;
    let connected = 0;
    for (const cfg of cfgs) {
      const conn = getConnection(cfg.id);
      if (conn && conn.statusSignal()() === 'connected') connected += 1;
    }
    if (connected === total) return total === 1 ? 'connected' : `${connected}/${total} up`;
    if (connected === 0)     return total === 1 ? 'offline'   : `0/${total} up`;
    return `${connected}/${total} up`;
  }

  function title(): string {
    if (consumerRevoked()) return 'OPBridge  -  access revoked. Click to re-pair.';
    return `OPBridge - ${summary()}. Click to connect.`;
  }

  return (
    <button
      type="button"
      class="bridge-status bridge-status-active opbridge-badge"
      data-state={dataState()}
      data-attention={attention()}
      onClick={openConnect}
      title={title()}
      aria-label="Open Connect"
    >
      <span class="led" aria-hidden="true" />
      <span class="lbl bridge-status-product">OPBridge</span>
      <span class="msg">{summary()}</span>
      <Show when={bridges().length > 1}>
        <span class="opbridge-badge-dots" aria-hidden="true">
          <For each={bridges()}>
            {(cfg) => {
              // Read inside the binding, version first: read once in the
              // callback body it was a constant, so the one "which machine
              // is down" glance never changed (2026-11-03 audit B-2).
              const state = () => {
                connectionsVersion();
                const conn = getConnection(cfg.id);
                return conn ? conn.statusSignal()() : 'unreachable';
              };
              return <span class="opbridge-badge-dot" data-state={state()} />;
            }}
          </For>
        </span>
      </Show>
    </button>
  );
}
